import { useTranslation } from '../../i18n/useTranslation';
import React, { useState } from 'react';
import { 
  Search, 
  ChevronDown, 
  Menu, 
  X,
  Bookmark,
  User,
  ExternalLink,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { AppView, RegionInfo, ToolItem } from '../../types';
import { YamooPayLogo } from '../common/YamooPayLogo';
import { MegaMenu } from './MegaMenu';
import { PWAInstallBanner } from './PWAInstallBanner';

interface GlobalHeaderProps {
  currentView: AppView;
  onNavigate: (view: AppView) => void;
  onSelectTool: (toolId: string) => void;
  selectedRegion: RegionInfo;
  onSelectRegion: (reg: RegionInfo) => void;
  allRegions: RegionInfo[];
  tools: ToolItem[];
  savedCount: number;
  onOpenSearch: () => void;
  onOpenWorkspace: () => void;
  onOpenAuthModal: () => void;
  onOpenMobileMenu?: () => void;
  isLoggedIn?: boolean;
}

export const GlobalHeader: React.FC<GlobalHeaderProps> = ({
  currentView,
  onNavigate,
  onSelectTool,
  selectedRegion,
  onSelectRegion,
  allRegions,
  tools,
  savedCount,
  onOpenSearch,
  onOpenWorkspace,
  onOpenAuthModal,
  onOpenMobileMenu,
  isLoggedIn = false,
}) => {
  const { t } = useTranslation();
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [regionMenuOpen, setRegionMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Workspace appears ONLY when isLoggedIn is true
  const showWorkspace = isLoggedIn;

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#E5E7EB] transition-colors">
      {/* PWA Install Banner or Toast */}
      <PWAInstallBanner />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-3">
          {/* Left: Brand Logo & Navigation */}
          <div className="flex items-center gap-8">
            <button
              onClick={() => onNavigate('homepage')}
              className="flex items-center text-left focus:outline-hidden cursor-pointer"
            >
              <YamooPayLogo size="md" />
            </button>

            {/* Desktop Navigation Links — Strictly: Tools | Money | Insights | Developers | About */}
            <nav className="hidden lg:flex items-center gap-1">
              {/* Tools with MegaMenu Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setMegaMenuOpen(true)}
              >
                <button
                  id="tools-menu-trigger"
                  aria-haspopup="true"
                  aria-expanded={megaMenuOpen}
                  aria-controls="yamoopay-tools-megamenu"
                  onClick={() => setMegaMenuOpen(!megaMenuOpen)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setMegaMenuOpen(true);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 cursor-pointer ${
                    megaMenuOpen || currentView === 'tools'
                      ? 'text-[#2563EB] font-bold bg-[#EFF6FF]'
                      : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
                  }`}
                >
                  <span>{t('nav.tools')}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform text-[#9CA3AF] ${megaMenuOpen ? 'rotate-180 text-[#2563EB]' : ''}`} />
                </button>
              </div>

              {/* Money */}
              <button
                onClick={() => onNavigate('currency-intelligence')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  currentView === 'currency-intelligence' || currentView === 'money-transfers'
                    ? 'text-[#1A1A1A] font-semibold bg-[#F3F4F6]'
                    : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
                }`}
              >
                Money
              </button>

              {/* Insights */}
              <button
                onClick={() => onNavigate('financial-guides')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  currentView === 'financial-guides' || currentView === 'editorial-article'
                    ? 'text-[#1A1A1A] font-semibold bg-[#F3F4F6]'
                    : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
                }`}
              >
                Insights
              </button>

              {/* Developers */}
              <button
                onClick={() => onNavigate('developer-portal')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  currentView === 'developer-portal'
                    ? 'text-[#1A1A1A] font-semibold bg-[#F3F4F6]'
                    : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
                }`}
              >
                Developers
              </button>

              {/* About */}
              <button
                onClick={() => onNavigate('discovery')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  currentView === 'discovery'
                    ? 'text-[#1A1A1A] font-semibold bg-[#F3F4F6]'
                    : 'text-[#4B5563] hover:text-[#1A1A1A] hover:bg-[#F9FAFB]'
                }`}
              >
                About
              </button>
            </nav>
          </div>

          {/* Right Header Utilities: Search | Region/Language | Sign in | Get started (or Workspace) */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Search Trigger */}
            <button
              onClick={onOpenSearch}
              className="flex items-center gap-2 px-3 py-1.5 bg-[#F9FAFB] hover:bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg text-xs text-[#6B7280] transition-colors cursor-pointer"
            >
              <Search className="w-3.5 h-3.5 text-[#1A1A1A]" />
              <span className="hidden md:inline font-normal">Search tools...</span>
              <kbd className="hidden md:inline-block px-1.5 py-0.5 text-[10px] font-mono font-semibold bg-white rounded border border-[#E5E7EB] text-[#6B7280]">
                ⌘K
              </kbd>
            </button>

            {/* Region / Language Selector */}
            <div className="relative">
              <button
                onClick={() => setRegionMenuOpen(!regionMenuOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs font-medium text-[#1A1A1A] transition-colors cursor-pointer"
                title={`Region: ${selectedRegion.name} (${selectedRegion.currency})`}
              >
                <span className="text-sm leading-none">{selectedRegion.flag}</span>
                <span className="font-mono text-xs hidden sm:inline">{selectedRegion.currency}</span>
                <ChevronDown className="w-3 h-3 text-[#9CA3AF]" />
              </button>

              {regionMenuOpen && (
                <div 
                  className="absolute right-0 mt-2 w-64 bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-50 p-1.5 space-y-0.5 animate-in fade-in duration-100 max-h-80 overflow-y-auto"
                  onMouseLeave={() => setRegionMenuOpen(false)}
                >
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF] border-b border-[#F3F4F6]">
                    Select Region & Currency
                  </div>
                  {allRegions.map((reg) => (
                    <button
                      key={reg.code}
                      onClick={() => {
                        onSelectRegion(reg);
                        setRegionMenuOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs text-left transition-colors cursor-pointer ${
                        selectedRegion.code === reg.code
                          ? 'bg-[#1A1A1A] text-white font-semibold'
                          : 'hover:bg-[#F3F4F6] text-[#1A1A1A]'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">{reg.flag}</span>
                        <span>{reg.name}</span>
                      </div>
                      <span className="font-mono text-[11px] opacity-75">{reg.currency}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Auth / Workspace Actions */}
            {showWorkspace ? (
              <button
                onClick={onOpenWorkspace}
                className="flex items-center gap-1.5 px-2.5 sm:px-3.5 py-1.5 bg-[#1A1A1A] hover:bg-black text-white rounded-lg text-xs font-semibold transition-colors cursor-pointer"
              >
                <Bookmark className="w-3.5 h-3.5 shrink-0" />
                <span className="hidden xs:inline sm:inline">{t('nav.workspace')}</span>
                {savedCount > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.2 bg-[#0E7044] text-white rounded-full text-[10px] font-bold">
                    {savedCount}
                  </span>
                )}
              </button>
            ) : (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={onOpenAuthModal}
                  className="px-2.5 sm:px-4 py-1.5 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-lg text-xs font-semibold transition-colors cursor-pointer shadow-xs whitespace-nowrap"
                >
                  Get started
                </button>
              </div>
            )}

            {/* Mobile Menu Hamburger */}
            <button
              onClick={() => {
                if (onOpenMobileMenu) {
                  onOpenMobileMenu();
                } else {
                  setMobileNavOpen(!mobileNavOpen);
                }
              }}
              className="lg:hidden p-2 text-[#1A1A1A] hover:bg-[#F3F4F6] rounded-lg transition-colors cursor-pointer"
              aria-label="Toggle menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mega Menu Dropdown */}
      <MegaMenu
        isOpen={megaMenuOpen}
        onClose={() => setMegaMenuOpen(false)}
        onSelectTool={onSelectTool}
        onViewAllTools={() => onNavigate('tools')}
        onOpenSearch={onOpenSearch}
        tools={tools}
      />

      {/* Mobile Navigation Drawer */}
      {mobileNavOpen && (
        <div className="lg:hidden bg-white border-b border-[#E5E7EB] px-4 py-4 space-y-3 animate-in fade-in slide-in-from-top-1 duration-150">
          <nav className="flex flex-col space-y-1 text-sm font-medium text-[#1A1A1A]">
            <button
              onClick={() => {
                onNavigate('tools');
                setMobileNavOpen(false);
              }}
              className="px-3 py-2 text-left rounded-lg hover:bg-[#F3F4F6] cursor-pointer"
            >
              Tools & Calculators
            </button>
            <button
              onClick={() => {
                onNavigate('currency-intelligence');
                setMobileNavOpen(false);
              }}
              className="px-3 py-2 text-left rounded-lg hover:bg-[#F3F4F6] cursor-pointer"
            >
              Money & Exchange Rates
            </button>
            <button
              onClick={() => {
                onNavigate('financial-guides');
                setMobileNavOpen(false);
              }}
              className="px-3 py-2 text-left rounded-lg hover:bg-[#F3F4F6] cursor-pointer"
            >
              Insights & Guides
            </button>
            <button
              onClick={() => {
                onNavigate('developer-portal');
                setMobileNavOpen(false);
              }}
              className="px-3 py-2 text-left rounded-lg hover:bg-[#F3F4F6] cursor-pointer"
            >
              Developers & API
            </button>
            <button
              onClick={() => {
                onNavigate('discovery');
                setMobileNavOpen(false);
              }}
              className="px-3 py-2 text-left rounded-lg hover:bg-[#F3F4F6] cursor-pointer"
            >
              About YamooPay
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};
